import { useEffect, useState } from 'react';

import { Button } from '@playbooks/ui/components/button';
import { DialogFooter, DialogHeader, DialogTitle } from '@playbooks/ui/components/dialog';
import { Field, FieldDescription, FieldLabel } from '@playbooks/ui/components/field';
import { Input } from '@playbooks/ui/components/input';
import { Textarea } from '@playbooks/ui/components/textarea';

import { useStore } from 'contexts';

const WorkspaceMcpFlowForm = ({ model, onPrev, onNext, onCreate }) => {
	const [values, setValues] = useState({ name: '', description: '', url: '', headers: '' });
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState('');
	const store = useStore();

	useEffect(
		() =>
			setValues({ name: model?.name || '', description: model?.description || '', url: model?.url || '', headers: '' }),
		[model],
	);
	const onSave = async event => {
		event.preventDefault();
		setSaving(true);
		setError('');
		try {
			let headers;
			if (values.headers.trim()) headers = JSON.parse(values.headers);
			const data: any = { name: values.name.trim(), description: values.description.trim(), url: values.url.trim() };
			if (headers !== undefined) data.headers = headers;
			const response = model
				? await store.updateRecord({ url: `/workspace/mcps/${model.uuid}`, data })
				: onCreate
					? { data: await onCreate({ ...data, headers: headers || {} }) }
					: await store.createRecord({ url: '/workspace/mcps', data: { ...data, headers: headers || {} } });
			await onNext(response.data);
		} catch (nextError) {
			setError(
				nextError instanceof SyntaxError
					? 'Headers must be a valid JSON object.'
					: nextError?.message || 'The MCP could not be saved.',
			);
		} finally {
			setSaving(false);
		}
	};

	return (
		<form onSubmit={onSave}>
			<DialogHeader>
				<DialogTitle>{model ? 'Edit MCP' : 'Add an MCP'}</DialogTitle>
			</DialogHeader>
			<div className='grid gap-5'>
				<Field>
					<FieldLabel>Name</FieldLabel>
					<Input required value={values.name} onChange={event => setValues({ ...values, name: event.target.value })} />
				</Field>
				<Field>
					<FieldLabel>Description</FieldLabel>
					<Textarea
						rows={3}
						value={values.description}
						onChange={event => setValues({ ...values, description: event.target.value })}
					/>
				</Field>
				<Field>
					<FieldLabel>Server URL</FieldLabel>
					<Input
						required
						type='url'
						placeholder='https://example.com/mcp'
						value={values.url}
						onChange={event => setValues({ ...values, url: event.target.value })}
					/>
					<FieldDescription>Remote Streamable HTTP MCP endpoint.</FieldDescription>
				</Field>
				<Field>
					<FieldLabel>Headers</FieldLabel>
					<Textarea
						rows={5}
						className='font-mono text-xs'
						placeholder={'{\n  "Authorization": "Bearer …"\n}'}
						value={values.headers}
						onChange={event => setValues({ ...values, headers: event.target.value })}
					/>
					<FieldDescription>
						{model?.hasCredentials
							? 'Leave blank to keep the saved headers.'
							: 'Optional JSON headers are encrypted and never shown again.'}
					</FieldDescription>
				</Field>
				{error ? <p className='text-sm text-danger'>{error}</p> : null}
			</div>
			<DialogFooter>
				<Button type='button' variant='secondary' onClick={onPrev}>
					Back
				</Button>
				<Button type='submit' isLoading={saving}>
					Save
				</Button>
			</DialogFooter>
		</form>
	);
};

export { WorkspaceMcpFlowForm };
